/**
 * Device-local Admin sign-in preferences (OWZ-80).
 *
 * Two non-secret values live in localStorage:
 *
 * - The remembered Google account: an opt-in presentation hint holding only
 *   the email and display name of the last authorized Google sign-in. It is a
 *   convenience for choosing an account, never authentication. Google
 *   credentials, Owaza session tokens, passwords, and authorization decisions
 *   are never stored here; the backend verifies every sign-in.
 * - The sign-in gate: why the last session ended. A deliberate sign-out,
 *   access rejection, or account switch blocks every automatic Google
 *   continuation; an in-app session expiry still allows a one-click Google
 *   prompt but never silent auto-selection. The gate stays until the next
 *   successful sign-in.
 */
import { googleLogout } from '@react-oauth/google';

export const REMEMBERED_ACCOUNT_KEY = 'owazaAdminRememberedAccount';
export const SIGN_IN_GATE_KEY = 'owazaAdminSignInGate';

export const SIGN_IN_GATES = Object.freeze({
  SIGNED_OUT: 'signed_out',
  SESSION_ENDED: 'session_ended',
});

const GATE_VALUES = new Set(Object.values(SIGN_IN_GATES));
const MAX_EMAIL_LENGTH = 254;
const MAX_NAME_LENGTH = 120;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+$/;

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode, quota); the preference is optional.
  }
};

export const getRememberedAccount = () => {
  const raw = readStorage(REMEMBERED_ACCOUNT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const email = typeof parsed?.email === 'string' ? parsed.email.trim() : '';
    if (!email || email.length > MAX_EMAIL_LENGTH || !EMAIL_PATTERN.test(email)) return null;
    const name = typeof parsed.name === 'string' ? parsed.name.trim().slice(0, MAX_NAME_LENGTH) : '';
    return { email, name };
  } catch {
    return null;
  }
};

/** Remember only the identity hint of an authorized admin; ignores anything else on `user`. */
export const rememberAccount = (user) => {
  const email = typeof user?.email === 'string' ? user.email.trim() : '';
  if (!email || email.length > MAX_EMAIL_LENGTH || !EMAIL_PATTERN.test(email)) {
    writeStorage(REMEMBERED_ACCOUNT_KEY, null);
    return;
  }
  const name = typeof user.name === 'string' ? user.name.trim().slice(0, MAX_NAME_LENGTH) : '';
  writeStorage(REMEMBERED_ACCOUNT_KEY, JSON.stringify({ email, name }));
};

export const forgetRememberedAccount = () => {
  writeStorage(REMEMBERED_ACCOUNT_KEY, null);
};

export const getSignInGate = () => {
  const value = readStorage(SIGN_IN_GATE_KEY);
  return GATE_VALUES.has(value) ? value : null;
};

/** Ask Google Identity Services not to auto-select an account on its next prompt. */
export const disableGoogleAutoSelect = () => {
  try {
    googleLogout();
  } catch {
    // The GIS script may not be loaded; the persisted gate still blocks auto-select.
  }
};

/**
 * Block automatic Google continuation until the next successful sign-in.
 * A stricter gate is never downgraded (signed_out outranks session_ended).
 */
export const closeSignInGate = (gate) => {
  if (!GATE_VALUES.has(gate)) return;
  if (gate === SIGN_IN_GATES.SESSION_ENDED && getSignInGate() === SIGN_IN_GATES.SIGNED_OUT) {
    disableGoogleAutoSelect();
    return;
  }
  writeStorage(SIGN_IN_GATE_KEY, gate);
  disableGoogleAutoSelect();
};

export const openSignInGate = () => {
  writeStorage(SIGN_IN_GATE_KEY, null);
};

/**
 * Map an AdminApp logout reason onto the gate it should leave behind.
 * Manual logouts arrive as click events or no reason at all.
 */
export const gateForLogoutReason = (reason) => (
  reason === 'inactivity' || reason === 'expired'
    ? SIGN_IN_GATES.SESSION_ENDED
    : SIGN_IN_GATES.SIGNED_OUT
);
